import { init } from "@paralleldrive/cuid2";

// The init function returns a custom createId function with the specified
// configuration. All configuration properties are optional.

/**
 * Generate a collision-resistant identifier using the configured options.
 *
 * The returned function produces a string identifier using the configured
 * random source, id length, and host fingerprint provided to the initializer.
 * Designed for use in distributed environments to reduce the chance of
 * collisions while remaining efficient for client-side usage.
 *
 * Configuration (as provided to the initializer):
 * - random: A function with the same API as Math.random() used as the
 *   entropy source (can be replaced with a cryptographically secure RNG).
 * - length: Length of the generated identifier string (default: 24).
 * - fingerprint: Optional host fingerprint to further reduce cross-host collisions.
 *
 * Notes:
 * - The function takes no arguments and returns a new id string on each call.
 * - IDs are opaque strings and should be treated as unique identifiers only.
 *
 * @function createId
 * @returns {string} A newly generated identifier string.
 *
 * @example
 * // Generate a new id
 * const id = createId();
 * // id => "a1b2c3d4e5f6g7h8i9j0k1l"
 */
export const createId = init({
  random: Math.random,
  length: 24, //length of returned id

  // A custom fingerprint for the host environment. This is used to help
  // prevent collisions when generating ids in a distributed system.
  // [ ] Update CUID fingerprint to get it from the database which is a user ids unique id from the server
  fingerprint: "pwa-client-development",
});

/**
 * Validates whether a string identifier was generated by CUID2
 * @param {*} id - The identifier to validate
 * @param {number} maxLength - Maximum allowed length (default: 24)
 * @returns {Object} Validation result with `isValid` boolean and `errors` array
 */
export function isValidCuid2(id, maxLength = 24) {
  const errors = [];

  // 1. Must be a string
  if (typeof id !== "string") {
    errors.push("Must be a string");
    return { isValid: false, errors };
  }

  // 2. Must not be empty
  if (id.length === 0) {
    errors.push("Cannot be empty");
    return { isValid: false, errors };
  }

  // 3. Must begin with a lowercase letter (a-z)
  const firstChar = id.charAt(0);
  if (!/^[a-z]$/.test(firstChar)) {
    errors.push("Must begin with a lowercase letter (a-z)");
  }

  // 4. Must not exceed maximum length
  if (id.length > maxLength) {
    errors.push(`Length ${id.length} exceeds maximum of ${maxLength}`);
  }

  // 5. CUID2 has a minimum length (typically at least 2 characters)
  if (id.length < 2) {
    errors.push("Too short - CUID2 must be at least 2 characters");
  }

  // 6. Must only contain base36 characters (0-9, a-z)
  const base36Regex = /^[0-9a-z]+$/;
  if (!base36Regex.test(id)) {
    errors.push("Contains invalid characters - only 0-9 and a-z allowed");

    // Show which characters are invalid
    const invalidChars = [
      ...new Set(id.split("").filter((char) => !/[0-9a-z]/.test(char))),
    ];
    if (invalidChars.length > 0) {
      errors.push(`Invalid characters found: ${invalidChars.join(", ")}`);
    }
  }

  // 7. Must not contain uppercase letters
  if (/[A-Z]/.test(id)) {
    errors.push("Contains uppercase letters - CUID2 is lowercase only");
  }

  // 8. Check for suspicious patterns that wouldn't appear in CUID2

  // CUID2 should have good entropy - check for repeated characters
  const hasLongRepetition = /(.)\1{4,}/.test(id);
  if (hasLongRepetition) {
    errors.push(
      "Contains suspicious repetition (5+ identical consecutive characters)"
    );
  }

  // CUID2 should appear random - sequential characters are suspicious
  const hasLongSequence =
    /(0123|1234|2345|3456|4567|5678|6789|abcd|bcde|cdef)/.test(id);
  if (hasLongSequence) {
    errors.push("Contains sequential characters (suspicious for CUID2)");
  }

  // 9. Entropy check - CUID2 should have reasonable character diversity
  const uniqueChars = new Set(id.split("")).size;
  const diversityRatio = uniqueChars / id.length;

  // If ID is long but has very low diversity, it's suspicious
  if (id.length >= 10 && diversityRatio < 0.3) {
    errors.push(
      `Low character diversity (${uniqueChars} unique chars in ${id.length} total)`
    );
  }

  //return boolean
  return errors.length === 0;

  //   return {
  //     isValid: errors.length === 0,
  //     errors: errors.length > 0 ? errors : null,
  //     details: {
  //       length: id.length,
  //       firstChar: firstChar,
  //       uniqueChars: uniqueChars,
  //       diversityRatio: parseFloat(diversityRatio.toFixed(2)),
  //     },
  //   };
}

// Example usage and tests
// console.log("=== Valid CUID2 Examples ===");
// console.log(validateCuid2("tz4a98xxat96iws9zmbrgj3a")); // typical CUID2
// console.log(validateCuid2("n1o2p3q4r5s6t7u8v9w0x1y2")); // valid format

// console.log("\n=== Invalid Examples ===");
// console.log(validateCuid2(123)); // not a string
// console.log(validateCuid2("Tz4a98xxat96iws9zmbrgj3a")); // starts with uppercase
// console.log(validateCuid2("9z4a98xxat96iws9zmbrgj3a")); // starts with number
// console.log(validateCuid2("tz4a98xxat96iws9zmbrgj3a_extra")); // contains underscore
// console.log(validateCuid2("tz4a98xxat96iws9zmbrgj3aEXTRA")); // contains uppercase
// console.log(validateCuid2("tz4a98xxat96iws9zmbrgj3a1234567890", 24)); // exceeds max length
// console.log(validateCuid2("aaaaaaaaaaaaaaaa")); // low diversity/repetition
// console.log(validateCuid2("a")); // too short
// console.log(validateCuid2("")); // empty

// Export for use in modules
// if (typeof module !== 'undefined' && module.exports) {
//   module.exports = validateCuid2;
// }
